import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import Button from '../../components/ui/Button';
import PersonalInformationSection from './components/PersonalInformationSection';
import RolePermissionsSection from './components/RolePermissionsSection';
import AccountStatusSection from './components/AccountStatusSection';
import SecuritySection from './components/SecuritySection';
import PermissionChangeModal from './components/PermissionChangeModal';
import StatusChangeModal from './components/StatusChangeModal';
import DeleteUserModal from './components/DeleteUserModal';
import SuccessNotification from './components/SuccessNotification';

const UserProfileEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location?.state?.userId || 1; // Default to user ID 1 for demo

  // State management
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  
  // Modal states
  const [permissionModal, setPermissionModal] = useState({ 
    isOpen: false, 
    newRole: '', 
    changes: [] 
  });
  const [statusModal, setStatusModal] = useState({ 
    isOpen: false, 
    newStatus: '' 
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const [successNotification, setSuccessNotification] = useState({ 
    isOpen: false, 
    message: '', 
    changes: [] 
  });

  // Mock user data for demonstration
  const mockUsers = {
    1: {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@party.com",
      role: "admin",
      status: "active",
      lastLogin: "2024-01-15T10:30:00Z",
      createdAt: "2024-01-01T08:00:00Z",
      loginHistory: [
        { date: "2024-01-15T10:30:00Z", ip: "192.168.1.100", location: "New York, NY" },
        { date: "2024-01-14T16:45:00Z", ip: "192.168.1.105", location: "New York, NY" },
        { date: "2024-01-13T09:15:00Z", ip: "192.168.1.100", location: "New York, NY" }
      ]
    },
    2: {
      id: 2,
      name: "Michael Rodriguez",
      email: "michael.rodriguez@party.com", 
      role: "manager",
      status: "active",
      lastLogin: "2024-01-14T16:45:00Z",
      createdAt: "2024-01-05T09:15:00Z",
      loginHistory: [
        { date: "2024-01-14T16:45:00Z", ip: "192.168.1.201", location: "Los Angeles, CA" },
        { date: "2024-01-13T11:20:00Z", ip: "192.168.1.201", location: "Los Angeles, CA" }
      ]
    }
  };

  // Role permissions mapping
  const rolePermissions = {
    admin: [
      'Full system access',
      'User management',
      'Role assignments',
      'System configuration',
      'Data export/import',
      'Analytics access'
    ],
    manager: [
      'User management (limited)',
      'Content management',
      'Report generation',
      'Team oversight'
    ],
    viewer: [
      'Read-only access',
      'Basic reports',
      'Profile management'
    ]
  };

  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const userData = mockUsers?.[userId];
      if (userData) {
        setUser(userData);
        setOriginalUser(userData);
        setFormData({
          name: userData?.name || '',
          email: userData?.email || '',
          role: userData?.role || 'viewer',
          status: userData?.status || 'active'
        });
      }
      
      setLoading(false);
    };

    loadUser();
  }, [userId]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Full name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select a user role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle role change with permission impact
  const handleRoleChange = (newRole) => {
    if (newRole === formData?.role) return;

    // Prevent admin from removing their own admin privileges
    if (originalUser?.role === 'admin' && originalUser?.id === 1 && newRole !== 'admin') {
      setErrors({ role: 'You cannot remove your own admin privileges' });
      return;
    }

    const currentPermissions = rolePermissions?.[formData?.role] || [];
    const newPermissions = rolePermissions?.[newRole] || [];
    
    const changes = {
      adding: newPermissions?.filter(p => !currentPermissions?.includes(p)),
      removing: currentPermissions?.filter(p => !newPermissions?.includes(p))
    };

    setPermissionModal({
      isOpen: true,
      newRole,
      changes
    });
  };

  // Confirm role change
  const confirmRoleChange = () => {
    setFormData(prev => ({ ...prev, role: permissionModal?.newRole }));
    setPermissionModal({ isOpen: false, newRole: '', changes: [] });
    setErrors(prev => ({ ...prev, role: '' }));
  };

  // Handle status change
  const handleStatusChange = (newStatus) => {
    if (newStatus === formData?.status) return;

    setStatusModal({
      isOpen: true,
      newStatus
    });
  };

  // Confirm status change
  const confirmStatusChange = () => {
    setFormData(prev => ({ ...prev, status: statusModal?.newStatus }));
    setStatusModal({ isOpen: false, newStatus: '' });
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSuccessNotification({
      isOpen: true,
      message: 'Password reset email sent successfully',
      changes: [`Password reset link sent to ${formData?.email}`]
    });
    
    setIsSubmitting(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Prepare change summary
      const changes = [];
      if (formData?.name !== originalUser?.name) {
        changes?.push(`Name changed from "${originalUser?.name}" to "${formData?.name}"`);
      }
      if (formData?.email !== originalUser?.email) {
        changes?.push(`Email changed from "${originalUser?.email}" to "${formData?.email}"`);
      }
      if (formData?.role !== originalUser?.role) {
        changes?.push(`Role changed from "${originalUser?.role}" to "${formData?.role}"`);
      }
      if (formData?.status !== originalUser?.status) {
        changes?.push(`Status changed from "${originalUser?.status}" to "${formData?.status}"`);
      }

      // Update user data
      const updatedUser = {
        ...user,
        name: formData?.name?.trim(),
        email: formData?.email?.toLowerCase()?.trim(),
        role: formData?.role,
        status: formData?.status
      };
      
      setUser(updatedUser);
      setOriginalUser(updatedUser);

      // Show success notification
      setSuccessNotification({
        isOpen: true,
        message: changes?.length > 0 ? 'User profile updated successfully' : 'No changes detected',
        changes
      });

    } catch (error) {
      setErrors({ 
        submit: 'Failed to update user profile. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Handle user deletion
  const handleDeleteUser = () => {
    // Prevent admin from deleting their own account
    if (originalUser?.role === 'admin' && originalUser?.id === 1) {
      setErrors({ delete: 'You cannot delete your own admin account' });
      return;
    }

    setDeleteModal({ isOpen: true });
  };

  // Confirm user deletion
  const confirmDeleteUser = async () => {
    setIsSubmitting(true);
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Navigate back to user management
      navigate('/user-management', { 
        state: { 
          message: `User "${user?.name}" has been deleted successfully` 
        }
      });

    } catch (error) {
      setErrors({ 
        delete: 'Failed to delete user. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
      setLoading(false);
      setDeleteModal({ isOpen: false });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground">User Not Found</h1>
              <p className="text-muted-foreground mt-2">The requested user could not be found.</p>
              <Button 
                onClick={() => navigate('/user-management')} 
                className="mt-4"
              >
                Back to User Management
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">User Profile Edit</h1>
            <p className="text-muted-foreground">
              Comprehensive user account modification with role management and permission control
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Personal Information */}
              <PersonalInformationSection
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldChange}
                originalUser={originalUser}
              />

              {/* Role & Permissions */}
              <RolePermissionsSection
                formData={formData}
                errors={errors}
                onRoleChange={handleRoleChange}
                rolePermissions={rolePermissions}
                originalUser={originalUser}
              />

              {/* Account Status */}
              <AccountStatusSection
                formData={formData}
                onStatusChange={handleStatusChange}
                originalUser={originalUser}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Security Section */}
              <SecuritySection
                user={user}
                onPasswordReset={handlePasswordReset}
                isSubmitting={isSubmitting}
              />

              {/* Action Buttons */}
              <div className="bg-white rounded-lg border border-border shadow-sm p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Actions</h3>
                <div className="space-y-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    iconName="Save"
                    iconPosition="left"
                    className="w-full"
                  >
                    {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => navigate('/user-management')}
                    disabled={isSubmitting}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    className="w-full"
                  >
                    Back to User Management
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={handleDeleteUser}
                    disabled={isSubmitting}
                    iconName="Trash2"
                    iconPosition="left"
                    className="w-full"
                  >
                    Delete User
                  </Button>
                </div>

                {errors?.submit && (
                  <p className="mt-3 text-sm text-red-600">
                    {errors?.submit}
                  </p>
                )}

                {errors?.delete && (
                  <p className="mt-3 text-sm text-red-600">
                    {errors?.delete}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Permission Change Modal */}
      <PermissionChangeModal
        isOpen={permissionModal?.isOpen}
        onClose={() => setPermissionModal({ isOpen: false, newRole: '', changes: [] })}
        onConfirm={confirmRoleChange}
        currentRole={formData?.role}
        newRole={permissionModal?.newRole}
        changes={permissionModal?.changes}
        userName={user?.name}
      />

      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={statusModal?.isOpen}
        onClose={() => setStatusModal({ isOpen: false, newStatus: '' })}
        onConfirm={confirmStatusChange}
        currentStatus={formData?.status}
        newStatus={statusModal?.newStatus}
        userName={user?.name}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={deleteModal?.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={confirmDeleteUser}
        userName={user?.name}
        isSubmitting={isSubmitting}
      />

      {/* Success Notification */}
      <SuccessNotification
        isOpen={successNotification?.isOpen}
        onClose={() => setSuccessNotification({ isOpen: false, message: '', changes: [] })}
        message={successNotification?.message}
        changes={successNotification?.changes}
      />

      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loading}
        message="Loading user profile..."
      />
    </div>
  );
};

export default UserProfileEdit;